import { useEffect, useRef } from "react";
import ReactSelect, { components } from "react-select";
import { useTheme } from "styled-components";
import { MdClose } from "react-icons/md";
import { Box, BoxProps } from "../Box";
import { Label } from "../Label";
import { ProfileOrb } from "../ProfileOrb";
import "./styles.css";

export type Option = {
  label: string;
  value: string | number;
  firstName: string;
  lastName: string;
  colour: string;
};

interface SelectProps extends BoxProps {
  options?: Option[] | Option;
  onChange: (newValue: Option[] | Option) => void;
  value: Option[];
  color?: string;
  label?: string;
  size?: "regular" | "large";
  name?: string;
  isMulti?: boolean;
  initialValue?: Option[];
  isDisabled?: boolean;
  /** Native tooltip — useful for explaining why a disabled field is disabled. */
  title?: string;
}

export const UserFilter = (props: SelectProps) => {
  const {
    options,
    onChange,
    value,
    label,
    name,
    size = "large",
    initialValue,
    isMulti,
    isDisabled,
    ...styleProps
  } = props;

  const { colors, radii, space } = useTheme() as any;
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);

  // react-select closes its own menu on the hidden input's blur; clicking a
  // non-focusable element outside the control (e.g. the Filter button)
  // doesn't reliably trigger that, so force it here instead.
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        selectRef.current?.blur();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const CustomInput = (inputProps: any) => {
    return (
      <>
        <components.Input className="main" {...inputProps} />
      </>
    );
  };

  const ClearIndicator = (indicatorProps: any) => (
    <components.ClearIndicator {...indicatorProps}>
      <MdClose size={16} color={colors.gray[50]} title="Clear user filter" />
    </components.ClearIndicator>
  );

  return (
    <Box {...styleProps} ref={containerRef}>
      {label && <Label>{label}</Label>}
      <ReactSelect
        ref={selectRef}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isClearable={!isMulti}
        options={options as any}
        onChange={onChange as any}
        value={value ?? initialValue}
        placeholder="User"
        name={name}
        closeMenuOnSelect
        components={{
          // Input: () => <></>,
          Input: CustomInput,
          ClearIndicator,
          SingleValue: ({ data }) => (
            <ProfileOrb
              firstName={data.firstName}
              lastName={data.lastName}
              colour={data.colour}
              top="-16px"
            />
          ),
          MultiValue: ({ data }) => (
            <ProfileOrb
              firstName={data.firstName}
              lastName={data.lastName}
              onClick={() =>
                onChange(value.filter(({ value }) => value !== data.value))
              }
              colour={data.colour}
              cursor="pointer"
              mr="-7px"
            />
          ),
        }}
        styles={{
          control: (base, { menuIsOpen }) => ({
            ...base,
            border: `2px solid ${
              menuIsOpen ? colors.blue.primary : colors.gray[30]
            }`,
            borderRadius: radii[0],
            height: 60,
            paddingLeft: space[1],
            fontSize: "14px",
            fontFamily: "Inter",
            boxShadow: "none",
            maxHeight: size === "regular" && 40,
            "::placeholder": {
              color: colors.gray[50],
            },
            ":hover": {
              border: `2px solid ${
                menuIsOpen ? colors.blue.primary : colors.gray[40]
              }`,
            },
            ...space,
          }),
          menu: (base) => ({
            ...base,
            minWidth: "220px",
            left: "-100px",
            zIndex:2,
          }),
          option: (base, { isSelected, isFocused }) => ({
            ...base,
            cursor: "pointer",
            fontFamily: "Inter",
            height: size === "regular" ? 40 : 60,
            display: "flex",
            alignItems: "center",
            minWidth: "220px",
            fontWeight: isSelected ? "bold" : "regular",
            backgroundColor:
              isFocused || isSelected ? colors.background : colors.white,
            color: colors.blue[100],
            ":hover": {
              backgroundColor: colors.background,
            },
            ...space,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: "100%",
          }),
          clearIndicator: (base) => ({
            ...base,
            height: "100%",
            alignItems: "center",
            padding: `0 ${space[1]}`,
            cursor: "pointer",
            color: colors.gray[50],
            ":hover": {
              color: colors.gray[60],
            },
          }),
          dropdownIndicator: (base) => ({
            ...base,
            height: "100%",
          }),
          indicatorSeparator: (base) => ({
            ...base,
            marginBottom: "0px",
            height: "46%",
          }),
        }}
      />
    </Box>
  );
};
